import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";

import Page from "../../../../components/Pages/Page";
import PageHeader from "../../../../components/PageHeader";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";
import { useTranscripts } from "../../hooks/useTranscripts";
import { Transcript, TranscriptFolder } from "../../types/transcripts";

const TranscriptDetail: React.FC = () => {
  const { transcriptId } = useParams();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const {
    completeTranscript,
    downloadTranscript,
    getTranscript,
    listFolders,
    updateTranscript,
    updateVisibility,
  } = useTranscripts();

  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [folders, setFolders] = useState<TranscriptFolder[]>([]);
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState<number | null>(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);

  const id = transcriptId ? Number(transcriptId) : NaN;
  const folderPathById = useMemo(() => {
    const folderById = new Map(
      folders.map((folder) => [folder.folder_id, folder]),
    );
    const resolvePath = (
      folder: TranscriptFolder,
      seen = new Set<number>(),
    ): string => {
      if (seen.has(folder.folder_id)) return folder.name;
      seen.add(folder.folder_id);
      if (!folder.parent_folder_id) return folder.name;
      const parent = folderById.get(folder.parent_folder_id);
      return parent
        ? `${resolvePath(parent, seen)} / ${folder.name}`
        : folder.name;
    };
    return new Map(
      folders.map((folder) => [folder.folder_id, resolvePath(folder)]),
    );
  }, [folders]);

  const load = useCallback(async () => {
    if (!workspaceId || !Number.isFinite(id)) return;
    setLoading(true);
    setError(null);
    try {
      const [transcriptRow, folderRows] = await Promise.all([
        getTranscript(workspaceId, id),
        listFolders(workspaceId),
      ]);
      setTranscript(transcriptRow);
      setFolders(folderRows);
      setTitle(transcriptRow.title);
      setFolderId(transcriptRow.folder_id ?? null);
      setText(transcriptRow.transcript_text ?? "");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load transcript",
      );
    } finally {
      setLoading(false);
    }
  }, [getTranscript, id, listFolders, workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!workspaceId || !transcript) return;
    setSaving(true);
    try {
      const updated = await updateTranscript(
        workspaceId,
        transcript.transcript_id,
        {
          title: title.trim(),
          folder_id: folderId,
          transcript_text: text,
        },
      );
      setTranscript(updated);
      setEditingTitle(false);
    } finally {
      setSaving(false);
    }
  };

  const saveCapturedText = async () => {
    if (!workspaceId || !transcript || transcript.segments.length === 0) return;
    setSaving(true);
    setError(null);
    const capturedText = transcript.segments
      .filter((segment) => segment.text)
      .map((segment) =>
        segment.speaker_label
          ? `${segment.speaker_label.trim()}: ${segment.text}`
          : segment.text,
      )
      .join("\n")
      .trim();
    try {
      const completed = await completeTranscript(
        workspaceId,
        transcript.transcript_id,
        { transcript_text: capturedText },
      );
      setTranscript(completed);
      setText(completed.transcript_text ?? capturedText);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not save captured text.",
      );
    } finally {
      setSaving(false);
    }
  };

  const makePrivate = async () => {
    if (!workspaceId || !transcript) return;
    setTranscript(
      await updateVisibility(workspaceId, transcript.transcript_id, "Private"),
    );
  };

  const publishToWorkspace = async () => {
    if (!workspaceId || !transcript) return;
    const updated = await updateVisibility(
      workspaceId,
      transcript.transcript_id,
      "Workspace",
      true,
    );
    setTranscript(updated);
    setConfirmOpen(false);
  };

  if (loading) {
    return (
      <Page>
        <Typography>Loading transcript…</Typography>
      </Page>
    );
  }

  if (!transcript) {
    return (
      <Page>
        <Alert color="danger">Transcript not found.</Alert>
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader>
        <Box>
          {editingTitle ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "stretch", sm: "center" }}
            >
              <Input
                autoFocus
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void save();
                  if (event.key === "Escape") {
                    setTitle(transcript.title);
                    setEditingTitle(false);
                  }
                }}
                sx={{ minWidth: { xs: "100%", sm: 360 } }}
              />
              <Button
                size="sm"
                onClick={save}
                loading={saving}
                disabled={!title.trim()}
              >
                Save title
              </Button>
              <Button
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => {
                  setTitle(transcript.title);
                  setEditingTitle(false);
                }}
              >
                Cancel
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                level="h1"
                onClick={() => setEditingTitle(true)}
                sx={{ cursor: "pointer" }}
              >
                {transcript.title}
              </Typography>
              <IconButton
                size="sm"
                variant="plain"
                onClick={() => setEditingTitle(true)}
                aria-label="Edit transcript title"
              >
                <EditIcon />
              </IconButton>
            </Stack>
          )}
          <Typography level="body-sm" color="neutral">
            Transcript detail
          </Typography>
        </Box>
        <Button
          component={Link}
          to={`/workspace/${workspaceId}/meridian/transcripts`}
          variant="outlined"
          color="neutral"
          startDecorator={<ArrowBackIcon />}
        >
          Back to Transcripts
        </Button>
      </PageHeader>
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <Card variant="outlined">
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Title</FormLabel>
                <Input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </FormControl>
              <FormControl sx={{ minWidth: 240 }}>
                <FormLabel>Folder</FormLabel>
                <Select
                  value={folderId == null ? "none" : String(folderId)}
                  onChange={(_event, value) =>
                    setFolderId(value === "none" ? null : Number(value))
                  }
                >
                  <Option value="none">No folder</Option>
                  {folders.map((folder) => (
                    <Option
                      key={folder.folder_id}
                      value={String(folder.folder_id)}
                    >
                      {folderPathById.get(folder.folder_id) ?? folder.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                variant="soft"
                color={
                  transcript.visibility === "Workspace" ? "primary" : "neutral"
                }
              >
                {transcript.visibility === "Workspace"
                  ? "Workspace public"
                  : "Private"}
              </Chip>
              <Chip variant="outlined">{transcript.status}</Chip>
              <Chip variant="outlined">{transcript.language}</Chip>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                startDecorator={<SaveIcon />}
                onClick={save}
                loading={saving}
                disabled={!title.trim()}
              >
                Save
              </Button>
              {transcript.status === "Failed" &&
                transcript.segments.length > 0 && (
                  <Button
                    variant="soft"
                    color="primary"
                    onClick={() => void saveCapturedText()}
                    loading={saving}
                  >
                    Save captured text
                  </Button>
                )}
              <Button
                variant="outlined"
                startDecorator={<DownloadIcon />}
                onClick={() =>
                  downloadTranscript(
                    workspaceId,
                    transcript.transcript_id,
                    transcript.title,
                  )
                }
              >
                Download .txt
              </Button>
              {transcript.visibility === "Workspace" ? (
                <Button
                  variant="soft"
                  color="neutral"
                  startDecorator={<LockIcon />}
                  onClick={makePrivate}
                >
                  Make private
                </Button>
              ) : (
                <Button
                  variant="soft"
                  color="primary"
                  startDecorator={<PublicIcon />}
                  onClick={() => setConfirmOpen(true)}
                >
                  Share with workspace
                </Button>
              )}
            </Stack>
          </Stack>
        </Card>

        <Card variant="outlined">
          <Stack spacing={1.5}>
            <Typography level="title-md">Transcript text</Typography>
            <Textarea
              minRows={18}
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          </Stack>
        </Card>

        {transcript.segments.length > 0 && (
          <Card variant="outlined">
            <Typography level="title-md">Segments</Typography>
            <Divider sx={{ my: 1 }} />
            <Stack spacing={1}>
              {transcript.segments.map((segment) => (
                <Box key={segment.segment_id}>
                  <Typography level="body-sm">{segment.text}</Typography>
                </Box>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <ModalDialog aria-labelledby="workspace-public-confirm-title">
          <Typography id="workspace-public-confirm-title" level="title-lg">
            Share transcript with workspace?
          </Typography>
          <Typography level="body-sm" sx={{ mt: 1 }}>
            Everyone with access to this workspace will be able to find, read,
            and download this text transcript. This does not make it public on
            the internet.
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button color="primary" onClick={publishToWorkspace}>
              Confirm workspace sharing
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Page>
  );
};

export default TranscriptDetail;
